/* tslint:disable */
/* eslint-disable */

export interface ActivityLog {
    id?: string;
    type?: string;
    message?: string;
    timestamp?: Date;
    icon?: string;
}

export interface AnalyticsDashboard {
    overview?: OverviewData;
    topProducts?: TopProduct[];
    topCustomers?: TopCustomer[];
    revenueByMonth?: RevenueByMonth[];
    aiSuggestions?: AISuggestion[];
}

export interface AuthenticationStats {
    totalRegistrations?: number;
    totalLogins?: number;
    activeUsers?: number;
    registrationBreakdown?: RegistrationBreakdown;
    loginBreakdown?: LoginBreakdown;
    registrationsByDay?: { [index: string]: number };
    loginsByDay?: { [index: string]: number };
    usersByCountry?: { [index: string]: number };
    successRate?: number;
    failedAttempts?: number;
}

export interface CategorySummary {
    name?: string;
    products?: number;
    revenue?: number;
    percentage?: number;
}

export interface DashboardStats extends Serializable {
    revenue?: MetricWithComparison;
    orders?: MetricWithComparison;
    customers?: MetricWithComparison;
    averageOrderValue?: MetricWithComparison;
    recentOrders?: RecentOrder[];
    recentActivity?: ActivityLog[];
    conversionRate?: number;
    deliveryRate?: number;
    repeatCustomerRate?: number;
    topProducts?: TopProductSummary[];
    topCategories?: CategorySummary[];
}

export interface KPIDashboard {
    period?: string;
    startDate?: string;
    endDate?: string;
    financial?: FinancialKPIs;
    sales?: SalesKPIs;
    customers?: CustomerKPIs;
    products?: ProductKPIs;
    trends?: TrendData;
    alerts?: Alert[];
    recommendations?: Recommendation[];
}

export interface MainDashboardStats {
    salesToday?: SalesToday;
    criticalAlerts?: CriticalAlerts;
}

export interface MetricWithComparison {
    current?: number;
    previous?: number;
    change?: number;
    changePercent?: number;
    trend?: string;
}

export interface OrderAnalytics {
    totalOrders?: number;
    totalRevenue?: number;
    averageOrderValue?: number;
    paidOrders?: number;
    deliveredOrders?: number;
    ordersByStatus?: { [index: string]: number };
    ordersByPaymentMethod?: { [index: string]: number };
    revenueByPaymentMethod?: { [index: string]: number };
    ordersByDeliveryMode?: { [index: string]: number };
    ordersByDay?: { [index: string]: number };
    revenueByDay?: { [index: string]: number };
    ordersByMonth?: { [index: string]: number };
    revenueByMonth?: { [index: string]: number };
    topSellingProducts?: TopProductDto[];
    conversionRate?: number;
    deliveryRate?: number;
    uniqueCustomers?: number;
    averageRevenuePerCustomer?: number;
}

export interface ProductAnalytics {
    totalProducts?: number;
    publishedProducts?: number;
    outOfStockProducts?: number;
    lowStockProducts?: number;
    topViewedProducts?: ProductStatsDto[];
    topSellingProducts?: ProductStatsDto[];
    topRatedProducts?: ProductStatsDto[];
    productsByCategory?: { [index: string]: number };
    salesByCategory?: { [index: string]: number };
    revenueByCategory?: { [index: string]: number };
    productsByStatus?: { [index: string]: number };
    viewsByDay?: { [index: string]: number };
    salesByDay?: { [index: string]: number };
    totalStockValue?: number;
    lowStockAlert?: LowStockProductDto[];
    averageRating?: number;
    totalReviews?: number;
}

export interface RecentOrder {
    id?: string;
    orderNumber?: string;
    customerName?: string;
    total?: number;
    status?: string;
    createdAt?: Date;
    items?: number;
}

export interface TopProductSummary {
    id?: string;
    name?: string;
    sold?: number;
    revenue?: number;
    image?: string;
}

export interface AuthResponse {
    accessToken?: string;
    refreshToken?: string;
    user?: User;
}

export interface User extends Serializable {
    id?: string;
    fullName?: string;
    username?: string;
    email?: string;
    phone?: string;
    enabled?: boolean;
    reasonDisabled?: string;
    lastConnection?: Date;
    role?: RoleEnum;
}

export interface UserResponse {
    id?: string;
    fullName?: string;
    username?: string;
    email?: string;
    phone?: string;
    role?: RoleEnum;
}

/**
 * Statistiques des utilisateurs
 */
export interface UserStatsDto {
    /**
     * Nombre total d'utilisateurs
     */
    totalUsers?: number;
    /**
     * Nombre d'utilisateurs actifs
     */
    activeUsers?: number;
    /**
     * Nombre d'utilisateurs inactifs
     */
    inactiveUsers?: number;
    /**
     * Nouveaux utilisateurs cette semaine
     */
    newUsersThisWeek?: number;
    /**
     * Nouveaux utilisateurs ce mois
     */
    newUsersThisMonth?: number;
    /**
     * Nombre d'administrateurs
     */
    adminUsers?: number;
    /**
     * Nombre de clients
     */
    customerUsers?: number;
    /**
     * Nombre de livreurs
     */
    delivererUsers?: number;
    /**
     * Nombre d'agents support
     */
    supportUsers?: number;
}

export interface Attribute extends Serializable {
    attributeName?: string;
    attributeCategory?: string;
    description?: string;
}

export interface AttributeType extends Serializable {
    id?: string;
    code?: string;
    name?: string;
    description?: string;
    displayOrder?: number;
    usedForVariants?: boolean;
    visibleOnFront?: boolean;
    values?: AttributeValue[];
}

export interface AttributeValue extends Serializable {
    id?: string;
    attributeTypeId?: string;
    attributeTypeName?: string;
    code?: string;
    value?: string;
    colorHex?: string;
    imageUrl?: string;
    displayOrder?: number;
    active?: boolean;
}

export interface CatalogueDashboard {
    productStats?: CatalogueProductStats;
    categoryStats?: CategoryStats;
    topSellingProducts?: DashboardProduct[];
    lowStockProducts?: DashboardProduct[];
    recentProducts?: DashboardProduct[];
}

export interface CatalogueProductStats {
    totalProducts?: number;
    lowStock?: number;
    outOfStock?: number;
}

export interface Category extends Serializable {
    id?: string;
    name?: string;
    type?: CategoryType;
    description?: string;
    parentCategoryId?: string;
    parentCategoryName?: string;
    slug?: string;
    children?: CategoryChild[];
}

export interface CategoryDetail {
    name?: string;
    slug?: string;
    children?: Category[];
}

export interface CategoryList {
    totalProducts?: number;
    totalPages?: number;
    currentPage?: number;
    itemsPerPage?: number;
    categories?: Category[];
}

export interface CategoryStats {
    totalCategories?: number;
    activeCategories?: number;
    emptyCategories?: number;
}

export interface DashboardProduct {
    id?: string;
    name?: string;
    categoryName?: string;
    price?: number;
    stock?: number;
    sold?: number;
    imagePath?: string;
    status?: string;
}

export interface ImageFile {
    content?: any;
    mimeType?: string;
}

export interface Media extends Serializable {
    id?: string;
    title?: string;
    mimeType?: string;
    content?: any;
    defaulted?: boolean;
}

export interface Product extends Serializable {
    id?: string;
    name?: string;
    description?: string;
    slug?: string;
    price?: number;
    stock?: number;
    rated?: number;
    sold?: number;
    discountedPercent?: number;
    isNew?: boolean;
    sku?: string;
    categoryName?: string;
    imagePath?: string;
    imageFile?: ImageFile;
}

export interface ProductAttribute extends Serializable {
    attribute?: Attribute;
    value?: string;
}

export interface ProductDetail extends Serializable {
    id?: string;
    name?: string;
    description?: string;
    price?: number;
    sku?: string;
    isNew?: boolean;
    stock?: number;
    sold?: number;
    views?: number;
    discountedPercent?: number;
    metaTitle?: string;
    metaKeywords?: string;
    metaDescription?: string;
    rated?: number;
    published?: boolean;
    status?: ProductStatusEnum;
    position?: number;
    slug?: string;
    category?: Category;
    ratings?: Rating[];
    wishes?: Wish[];
    productAttributes?: ProductAttribute[];
    medias?: Media[];
    variants?: ProductVariant[];
}

export interface ProductList {
    totalProducts?: number;
    totalPages?: number;
    currentPage?: number;
    itemsPerPage?: number;
    products?: Product[];
}

export interface ProductVariant extends Serializable {
    id?: string;
    productId?: string;
    sku?: string;
    name?: string;
    price?: number;
    compareAtPrice?: number;
    effectivePrice?: number;
    stock?: number;
    sold?: number;
    weight?: number;
    barcode?: string;
    available?: boolean;
    inStock?: boolean;
    displayOrder?: number;
    imageUrl?: string;
    attributes?: AttributeValue[];
    default?: boolean;
}

export interface Stock extends Serializable {
    id?: string;
    quantity?: number;
    action?: StockEnum;
    createdAt?: Date;
}

export interface AttributeRequest {
    attribute?: string;
    value?: string;
}

export interface CategoryRequest {
    name?: string;
    description?: string;
    categoryType?: string;
    parent?: string;
    children?: string[];
    mainMenu?: boolean;
}

export interface CreateProductRequest {
    attributes?: string;
    name?: string;
    description?: string;
    price?: number;
    stock?: number;
    discountedPercent?: number;
    metaTitle?: string;
    metaDescription?: string;
    metaKeywords?: string;
    status?: string;
    published?: boolean;
    category?: string;
    files?: MultipartFile[];
}

export interface ProductFilterRequest {
    query?: string;
    minPrice?: number;
    maxPrice?: number;
    categorySlug?: string;
    categorySlugs?: string[];
    inStock?: boolean;
    outOfStock?: boolean;
    status?: ProductStatusEnum;
    published?: boolean;
    featured?: boolean;
    isNew?: boolean;
    bestSeller?: boolean;
    topRated?: boolean;
    onSale?: boolean;
    minDiscount?: number;
    minSold?: number;
    minRating?: number;
    sortBy?: string;
    sortDirection?: string;
    page?: number;
    size?: number;
}

export interface ProductStatusAndPublishedRequest extends Serializable {
    published?: boolean;
    status?: string;
}

export interface ProductUpdateRequest extends Serializable {
    name?: string;
    description?: string;
    price?: number;
    discountedPercent?: number;
    metaTitle?: string;
    metaKeywords?: string;
    metaDescription?: string;
    published?: boolean;
    status?: ProductStatusEnum;
    position?: number;
    category?: string;
}

export interface ProductVariantRequest {
    sku?: string;
    name?: string;
    price?: number;
    compareAtPrice?: number;
    stock?: number;
    weight?: number;
    barcode?: string;
    isDefault?: boolean;
    available?: boolean;
    attributeValueIds?: string[];
}

export interface StockAddRequest {
    product?: string;
    quantity?: number;
    action?: StockEnum;
}

export interface Cart extends Serializable {
    id?: string;
    product?: Product;
    quantity?: number;
}

export interface CommerceDashboardStats {
    salesStats?: SalesStats;
    productStats?: ProductStats;
    topSellingProducts?: TopSellingProduct[];
    recentOrders?: RecentOrder[];
    pendingOrders?: PendingOrder[];
    problematicOrders?: ProblematicOrder[];
    revenueTrend?: TrendDataPoint[];
    ordersTrend?: TrendDataPoint[];
    couponStats?: CouponStats;
}

export interface Coupon extends Serializable {
    id?: string;
    code?: string;
    type?: CouponType;
    discount?: number;
    fixedAmount?: number;
    minimumOrderAmount?: number;
    maxUsages?: number;
    currentUsages?: number;
    startDate?: Date;
    expiryDate?: Date;
    used?: boolean;
}

export interface CouponList extends Serializable {
    total?: number;
    totalPages?: number;
    currentPage?: number;
    itemsPerPage?: number;
    coupons?: Coupon[];
}

export interface CouponOrder {
    code?: string;
    order?: string;
}

export interface CouponStats {
    activeCoupons?: number;
    expiredCoupons?: number;
    usedCoupons?: number;
}

export interface CouponValidationResult {
    code?: string;
    isValid?: boolean;
    message?: string;
    discountAmount?: number;
    percentageDiscount?: number;
    type?: string;
    minimumOrderAmount?: number;
    maxUsages?: number;
    currentUsages?: number;
    startDate?: Date;
    expiryDate?: Date;
}

export interface Delivery extends Serializable {
    id?: string;
    delivererName?: string;
    delivererPhone?: string;
    shippedDate?: Date;
    regionId?: string;
    regionRegionName?: string;
}

export interface EcommerceSettings extends Serializable {
    id?: string;
    currency?: string;
    currencySymbol?: string;
    shippingFeeAbidjan?: number;
    shippingFeeOutsideAbidjan?: number;
    shippingFeeInternational?: number;
    freeShippingThreshold?: number;
    taxRate?: number;
    taxEnabled?: boolean;
    storeEmail?: string;
    storePhone?: string;
    storeAddress?: string;
    storeName?: string;
    storeDescription?: string;
    maintenanceMode?: boolean;
    maintenanceMessage?: string;
    deliveryTimeAbidjan?: number;
    deliveryTimeOutsideAbidjan?: number;
    abidjanCities?: string;
}

export interface Order extends Serializable {
    id?: string;
    orderNumber?: string;
    status?: OrderStatus;
    subtotal?: number;
    discountCoupon?: number;
    total?: number;
    deliveryCosts?: number;
    orderDate?: Date;
    carts?: Cart[];
    customer?: Customer;
    deliveryCity?: string;
    deliveryNeighborhood?: string;
    deliveryStreetAddress?: string;
    deliveryApartmentInfo?: string;
    deliveryPostalCode?: string;
    coupon?: Coupon;
    paymentMethod?: string;
    deliveryMode?: string;
    deliveryPhone?: string;
}

export interface OrderCreationResponse extends Serializable {
    orderId?: string;
    orderNumber?: string;
    customerId?: string;
    addressId?: string;
    accessToken?: string;
    refreshToken?: string;
    guestAccessToken?: string;
}

export interface OrderDetail {
    order?: Order;
    tracking?: OrderTrackingStep[];
}

export interface OrderList {
    totalElements?: number;
    totalPages?: number;
    currentPage?: number;
    itemsPerPage?: number;
    orders?: Order[];
}

export interface OrderTrackingStep extends Serializable {
    id?: string;
    status?: OrderStatus;
    actor?: string;
    comment?: string;
    trackingDate?: Date;
}

export interface PendingOrder {
    orderId?: string;
    customerName?: string;
    amount?: number;
}

export interface ProblematicOrder {
    orderId?: string;
    issueDescription?: string;
}

export interface ProductStats {
    totalProducts?: number;
    lowStockProducts?: number;
    topSellingCount?: number;
    topSellingRevenue?: number;
    topCategoriesCount?: number;
    categoriesWithSales?: number;
}

export interface SalesStats {
    totalRevenue?: number;
    revenueGrowth?: number;
    totalOrders?: number;
    pendingOrders?: number;
    completedOrders?: number;
    averageOrderValue?: number;
    aovGrowth?: number;
    conversionRate?: number;
    totalVisitors?: number;
    convertedVisitors?: number;
}

export interface TopSellingProduct {
    productId?: string;
    productName?: string;
    categoryName?: string;
    quantitySold?: number;
    revenue?: number;
    price?: number;
}

export interface TrendDataPoint {
    date?: string;
    value?: number;
}

export interface UserOrderStats {
    totalSpent?: number;
    totalOrders?: number;
    averageOrderValue?: number;
    completedOrders?: number;
    cancelledOrders?: number;
    returnedOrders?: number;
    lastOrderDate?: Date;
    cancellationRate?: number;
}

export interface WhatsAppStats extends Serializable {
    totalSubscribers?: number;
    activeSubscribers?: number;
    inactiveSubscribers?: number;
    todaySubscribers?: number;
    weekSubscribers?: number;
    monthSubscribers?: number;
    growthRate?: number;
    guestSubscribers?: number;
    customerSubscribers?: number;
    adminSubscribers?: number;
}

export interface WhatsAppSubscriber extends Serializable {
    id?: string;
    phoneNumber?: string;
    countryCode?: string;
    isActive?: boolean;
    subscribedAt?: Date;
    unsubscribedAt?: Date;
    lastMessageSentAt?: Date;
    totalMessagesSent?: number;
    source?: string;
    notes?: string;
    userId?: string;
    userFullName?: string;
    subscriberType?: SubscriberType;
    consentGiven?: boolean;
    ipAddress?: string;
}

export interface CouponAddRequest extends Serializable {
    type?: CouponType;
    discount?: number;
    fixedAmount?: number;
    minimumOrderAmount?: number;
    maxUsages?: number;
    startDate?: Date;
    expiryDate?: Date;
}

export interface DeliveryCourrierRequest extends Serializable {
    deliveryId?: string;
    deliveryCode?: string;
    courrierName?: string;
    courrierPhone?: string;
    shippingDate?: Date;
}

export interface DeliveryCreateRequest {
    order?: OrderEntity;
    customerName?: string;
    customerEmail?: string;
    customerPhone?: string;
    customerAdditionalPhone?: string;
    apartmentInfo?: string;
    streetAddress?: string;
    neighborhood?: string;
    city?: string;
    postalCode?: string;
}

export interface DeliveryStatusRequest {
    deliveryId?: string;
    status?: string;
    comment?: string;
}

export interface EcommerceSettingsUpdateRequest extends Serializable {
    currency?: string;
    currencySymbol?: string;
    shippingFeeAbidjan?: number;
    shippingFeeOutsideAbidjan?: number;
    shippingFeeInternational?: number;
    freeShippingThreshold?: number;
    taxRate?: number;
    taxEnabled?: boolean;
    storeEmail?: string;
    storePhone?: string;
    storeAddress?: string;
    storeName?: string;
    storeDescription?: string;
    maintenanceMode?: boolean;
    maintenanceMessage?: string;
    deliveryTimeAbidjan?: number;
    deliveryTimeOutsideAbidjan?: number;
    abidjanCities?: string;
}

export interface OrderAddRequest extends Serializable {
    carts?: CartItem[];
    couponCode?: string;
    addressId?: string;
    deliveryMode?: string;
    paymentMethod?: string;
    guestInfo?: GuestInfo;
}

export interface OrderCommentRequest extends Serializable {
    comment?: string;
}

export interface OrderStatusUpdateRequest extends Serializable {
    status?: string;
    comment?: string;
}

export interface Address extends Serializable {
    id?: string;
    label?: string;
    isDefault?: boolean;
    phoneNumber?: string;
    streetAddress?: string;
    apartmentInfo?: string;
    neighborhood?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    latitude?: number;
    longitude?: number;
}

export interface Customer extends Serializable {
    id?: string;
    fullName?: string;
    username?: string;
}

export interface Wish extends Serializable {
    id?: string;
    customer?: Customer;
    product?: Product;
}

export interface AddressAddRequest extends Serializable {
    label?: string;
    streetAddress?: string;
    apartmentInfo?: string;
    city?: string;
    postalCode?: string;
    neighborhood?: string;
    country?: string;
    latitude?: number;
    longitude?: number;
    phone?: string;
    additionalPhone?: string;
    isDefault?: boolean;
}

export interface WishRequest {
    productId?: number;
    action?: ActionEnum;
}

export interface GeoLocation extends Serializable {
    region_code?: string;
    country_name?: string;
    country_code?: string;
    postal?: string;
    ip?: string;
    city?: string;
    region?: string;
    country?: string;
    latitude?: number;
    longitude?: number;
    timezone?: string;
    org?: string;
    asn?: string;
}

export interface Advertisement extends Serializable {
    active?: boolean;
    message?: string;
    position?: AdvertisementPositionEnum;
    title?: string;
    img?: any;
    mimeType?: string;
    originalFilename?: string;
    id?: string;
}

export interface AdvertisementCustomer {
    carousels?: Advertisement[];
    largeBanner?: Advertisement;
    leftBanner?: Advertisement;
    rightBanner?: Advertisement;
}

export interface AdvertisementList extends Serializable {
    totalElements?: number;
    totalPages?: number;
    numberOfElements?: number;
    size?: number;
    content?: Advertisement[];
}

export interface Banner extends Serializable {
    url?: string;
    type?: string;
}

export interface CategoryCard {
    name?: string;
    categoryCard?: boolean;
    cardPosition?: number;
    type?: CategoryType;
    slug?: string;
}

export interface CategoryChild {
    id?: string;
    name?: string;
    slug?: string;
}

export interface CategoryMenu {
    id?: string;
    name?: string;
    mainMenu?: boolean;
    type?: CategoryType;
    slug?: string;
    children?: CategoryChild[];
}

export interface Menu extends Serializable {
    id?: string;
    label?: string;
    url?: string;
    position?: number;
    published?: boolean;
    icon?: string;
    openInNewTab?: boolean;
}

export interface MenuDetail extends Serializable {
    menu?: Menu;
    children?: Menu[];
}

export interface PageSection extends Serializable {
    id?: string;
    title?: string;
    slug?: string;
    description?: string;
    type?: string;
    position?: number;
    published?: boolean;
    productsCount?: number;
    banners?: Banner[];
}

export interface PageSectionDetail extends Serializable {
    id?: string;
    title?: string;
    slug?: string;
    description?: string;
    type?: string;
    position?: number;
    published?: boolean;
    products?: Product[];
    banners?: Banner[];
}

export interface AddImageAdvertisementRequest {
    title?: string;
    position?: string;
    img?: MultipartFile;
    active?: boolean;
}

export interface AddTextAdvertisementRequest extends Serializable {
    active?: boolean;
    message?: string;
    position?: string;
    title?: string;
}

export interface AdvertisementStatusUpdateRequest {
    active?: boolean;
}

export interface BannerAddRequest {
    url?: string;
    type?: string;
}

export interface BannerRequest extends Serializable {
    url?: string;
    type?: string;
    position?: number;
}

export interface MenuRequest extends Serializable {
    label?: string;
    url?: string;
    position?: number;
    published?: boolean;
    icon?: string;
    openInNewTab?: boolean;
    category?: string;
}

export interface PageSectionAddRequest extends Serializable {
    title?: string;
    type?: string;
    description?: string;
    position?: number;
    published?: boolean;
    banners?: BannerRequest[];
}

export interface WhatsAppSubscribeRequest {
    phoneNumber?: string;
    source?: string;
    consentGiven?: boolean;
}

export interface OrderEmailData {
    customerEmail?: string;
    customerName?: string;
    orderNumber?: string;
    orderStatus?: OrderStatus;
    orderDate?: Date;
    subtotal?: number;
    deliveryCosts?: number;
    total?: number;
    deliveryAddress?: string;
    items?: OrderItemEmailData[];
    trackingUrl?: string;
    statusComment?: string;
}

export interface PaymentEmailData {
    customerEmail?: string;
    customerName?: string;
    orderNumber?: string;
    amount?: number;
    paymentMethod?: PaymentMethod;
    paymentStatus?: PaymentStatus;
    paymentDate?: Date;
    transactionId?: string;
    reference?: string;
}

export interface Rating extends Serializable {
    createdAt?: Date;
    user?: Customer;
    rating?: number;
    review?: string;
}

export interface AddCouponRequest {
    coupon?: string;
}

export interface AddItemRequest {
    cart?: string;
    product?: string;
    quantity?: number;
}

export interface AuthenticationResponse {
    accessToken?: string;
    refreshToken?: string;
}

export interface LoginRequest {
    username?: string;
    password?: string;
}

export interface RatingRequest {
    cart?: string;
    rating?: number;
    review?: string;
}

export interface RegisterRequest {
    fullName?: string;
    email?: string;
    phone?: string;
    role?: string;
    password?: string;
    termeOfService?: boolean;
}

export interface ReorderMediasRequest {
    order?: MediaOrder[];
}

export interface ResetPasswordRequest {
    email?: string;
    password?: string;
    token?: string;
}

export interface UpdateItemRequest {
    cartItem?: number;
    action?: string;
}

export interface UpdateOrderRequest {
    discountCoupon?: number;
    deliveryCosts?: number;
    region?: string;
    city?: string;
    streetAddress?: string;
    mobile?: string;
    additionalPhoneNumber?: string;
}

export interface UpdateProductMediasRequest {
    files?: MultipartFile[];
}

export interface UpdateProfileRequest {
    fullName?: string;
    email?: string;
    phone?: string;
}

export interface PaymentRequest {
    paymentMethod?: PaymentMethod;
    provider?: string;
    phoneNumber?: string;
    customerEmail?: string;
    customerName?: string;
    returnUrl?: string;
    callbackUrl?: string;
    description?: string;
}

export interface PaymentResponse {
    paymentId?: string;
    transactionId?: string;
    status?: PaymentStatus;
    amount?: number;
    redirectUrl?: string;
    paymentToken?: string;
    message?: string;
    orderNumber?: string;
    instructions?: string;
}

export interface PaymentWebhookRequest {
    event?: string;
    transactionId?: string;
    reference?: string;
    status?: string;
    amount?: number;
    description?: string;
    customData?: CustomData;
    signature?: string;
}

export interface Author extends Serializable {
    username?: string;
    fullName?: string;
}

export interface Complaint extends Serializable {
    id?: string;
    title?: string;
    orderId?: string;
    orderReference?: string;
    customer?: Customer;
    description?: string;
    category?: ComplaintCategoryEnum;
    priority?: ComplaintPriorityEnum;
    response?: string;
    status?: ComplaintStatusEnum;
    complaintDate?: Date;
    responseDate?: Date;
    administrator?: string;
    assignedToId?: string;
    assignedToName?: string;
    assignedAt?: Date;
    satisfactionRating?: number;
    satisfactionComment?: string;
    satisfactionDate?: Date;
    resolvedAt?: Date;
    closedAt?: Date;
    medias?: ComplaintMedia[];
    comments?: ComplaintComment[];
    history?: ComplaintHistory[];
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ComplaintAnalytics {
    totalComplaints?: number;
    newComplaints?: number;
    assignedComplaints?: number;
    inProgressComplaints?: number;
    resolvedComplaints?: number;
    closedComplaints?: number;
    complaintsByPriority?: { [P in ComplaintPriorityEnum]?: number };
    complaintsByCategory?: { [P in ComplaintCategoryEnum]?: number };
    complaintsByStatus?: { [P in ComplaintStatusEnum]?: number };
    averageResolutionTimeHours?: number;
    averageFirstResponseTimeHours?: number;
    satisfactionRatingAverage?: number;
    complaintsThisWeek?: number;
    complaintsThisMonth?: number;
    weekOverWeekChange?: number;
    totalAgents?: number;
    complaintsByAgent?: { [index: string]: number };
}

export interface ComplaintComment {
    id?: string;
    complaintId?: string;
    authorId?: string;
    authorName?: string;
    authorRole?: string;
    content?: string;
    isInternal?: boolean;
    commentDate?: Date;
    createdAt?: Date;
}

export interface ComplaintHistory {
    id?: string;
    complaintId?: string;
    userId?: string;
    userName?: string;
    actionType?: string;
    oldValue?: string;
    newValue?: string;
    oldStatus?: ComplaintStatusEnum;
    newStatus?: ComplaintStatusEnum;
    description?: string;
    actionDate?: Date;
}

export interface ComplaintMedia extends Serializable {
    id?: string;
    title?: string;
    mimeType?: string;
    content?: any;
    fileName?: string;
}

export interface GuestComplaint {
    id?: string;
    ticketNumber?: string;
    guestName?: string;
    email?: string;
    phone?: string;
    subject?: string;
    description?: string;
    category?: ComplaintCategoryEnum;
    status?: ComplaintStatusEnum;
    response?: string;
    complaintDate?: Date;
    responseDate?: Date;
    administratorName?: string;
    emailVerified?: boolean;
    createdAt?: Date;
}

export interface ComplaintAddRequest extends Serializable {
    title?: string;
    orderId?: string;
    description?: string;
    files?: MultipartFile[];
}

export interface ComplaintAssignRequest {
    agentId?: string;
}

export interface ComplaintCommentRequest {
    content?: string;
    isInternal?: boolean;
}

export interface ComplaintRequest {
    order?: number;
    title?: string;
    description?: string;
}

export interface ComplaintResponseRequest {
    response?: string;
}

export interface ComplaintSatisfactionRequest {
    rating?: number;
    comment?: string;
}

export interface ComplaintStatusRequest {
    status?: string;
    response?: string;
}

export interface ComplaintUpdateRequest extends Serializable {
    description?: string;
    newFiles?: MultipartFile[];
}

export interface GuestComplaintRequest {
    guestName?: string;
    email?: string;
    phone?: string;
    subject?: string;
    description?: string;
    category?: ComplaintCategoryEnum;
}

export interface GuestComplaintResponseRequest {
    response?: string;
    administratorName?: string;
}

export interface OverviewData {
    totalRevenue?: number;
    revenueGrowth?: number;
    totalOrders?: number;
    ordersGrowth?: number;
    totalCustomers?: number;
    customersGrowth?: number;
    averageOrderValue?: number;
    aovGrowth?: number;
}

export interface TopProduct {
    name?: string;
    sales?: number;
    revenue?: number;
    growth?: number;
}

export interface TopCustomer {
    name?: string;
    orders?: number;
    revenue?: number;
    lastOrder?: string;
}

export interface RevenueByMonth {
    month?: string;
    revenue?: number;
    orders?: number;
}

export interface AISuggestion {
    category?: string;
    title?: string;
    description?: string;
    impact?: string;
    potentialIncrease?: number;
}

export interface RegistrationBreakdown {
    emailOnly?: number;
    phoneOnly?: number;
    emailAndPhone?: number;
    emailPercentage?: number;
    phonePercentage?: number;
    bothPercentage?: number;
}

export interface LoginBreakdown {
    viaEmail?: number;
    viaPhone?: number;
    emailPercentage?: number;
    phonePercentage?: number;
}

export interface Serializable {
}

export interface FinancialKPIs {
    revenue?: MetricWithComparison;
    netMargin?: MetricWithComparison;
    netMarginRate?: MetricWithComparison;
    cashFlow?: MetricWithComparison;
    accountsReceivable?: number;
    paymentMethods?: PaymentMethodBreakdown;
    costs?: CostBreakdown;
}

export interface SalesKPIs {
    totalOrders?: MetricWithComparison;
    averageOrderValue?: MetricWithComparison;
    conversionRate?: MetricWithComparison;
    paymentRate?: MetricWithComparison;
    cancellationRate?: MetricWithComparison;
    orderStatus?: OrderStatusBreakdown;
    totalItemsSold?: number;
    avgItemsPerOrder?: number;
}

export interface CustomerKPIs {
    totalCustomers?: MetricWithComparison;
    newCustomers?: MetricWithComparison;
    repeatCustomers?: MetricWithComparison;
    repeatCustomerRate?: number;
    averageLTV?: MetricWithComparison;
    segmentation?: CustomerSegmentation;
    cartAbandonmentRate?: number;
}

export interface ProductKPIs {
    totalProducts?: number;
    outOfStockProducts?: number;
    lowStockProducts?: number;
    totalStockValue?: number;
    avgConversionRate?: number;
    topSellingProducts?: ProductPerformance[];
    topViewedProducts?: ProductPerformance[];
    topCategories?: CategoryPerformance[];
}

export interface TrendData {
    revenueByDay?: DailyDataPoint[];
    ordersByDay?: DailyDataPoint[];
    aovByDay?: DailyDataPoint[];
    ltvByDay?: DailyDataPoint[];
}

export interface Alert {
    type?: string;
    category?: string;
    message?: string;
    actionRequired?: string;
    priority?: number;
}

export interface Recommendation {
    category?: string;
    title?: string;
    description?: string;
    expectedImpact?: string;
    actions?: string[];
}

export interface SalesToday {
    revenue?: number;
    orders?: number;
}

export interface CriticalAlerts {
    pendingOrders?: number;
    lowStockProducts?: number;
    openComplaints?: number;
}

export interface TopProductDto {
    productId?: string;
    productName?: string;
    quantitySold?: number;
    totalRevenue?: number;
}

export interface ProductStatsDto {
    productId?: string;
    productName?: string;
    categoryName?: string;
    views?: number;
    sold?: number;
    rating?: number;
    price?: number;
    totalRevenue?: number;
    stock?: number;
}

export interface LowStockProductDto {
    productId?: string;
    productName?: string;
    currentStock?: number;
    sold?: number;
    status?: string;
}

export interface MultipartFile extends InputStreamSource {
    name?: string;
    bytes?: any;
    empty?: boolean;
    resource?: Resource;
    size?: number;
    contentType?: string;
    originalFilename?: string;
}

export interface OrderEntity extends AbstractEntity, Serializable {
    orderNumber?: string;
    status?: OrderStatus;
    subtotal?: number;
    total?: number;
    deliveryCosts?: number;
    discountCoupon?: number;
    /**
     * @deprecated
     */
    addressId?: string;
    paymentMethod?: PaymentMethod;
    deliveryMode?: DeliveryMode;
    orderDate?: Date;
    carts?: CartEntity[];
    customer?: CustomerEntity;
    coupon?: CouponEntity;
    deliveries?: DeliveryEntity[];
    payments?: PaymentEntity[];
    trackingSteps?: OrderTrackingStepEntity[];
    guestAccessToken?: string;
    guestEmail?: string;
    shippingAddress?: OrderAddressEntity;
    paid?: boolean;
    delivered?: boolean;
}

export interface CartItem extends Serializable {
    productId?: string;
    quantity?: number;
}

export interface GuestInfo extends Serializable {
    fullName?: string;
    email?: string;
    phone?: string;
    additionalPhone?: string;
    streetAddress?: string;
    apartmentInfo?: string;
    city?: string;
    postalCode?: string;
    neighborhood?: string;
    country?: string;
    latitude?: number;
    longitude?: number;
}

export interface OrderItemEmailData {
    productName?: string;
    quantity?: number;
    unitPrice?: number;
    totalPrice?: number;
    productImageUrl?: string;
}

export interface MediaOrder {
    mediaId?: string;
    displayOrder?: number;
}

export interface CustomData {
    orderId?: string;
    orderNumber?: string;
}

export interface PaymentMethodBreakdown {
    mobileMoney?: number;
    card?: number;
    cash?: number;
    bankTransfer?: number;
}

export interface CostBreakdown {
    discounts?: number;
    deliveryCosts?: number;
    refunds?: number;
    gatewayFees?: number;
    costOfGoodsSold?: number;
    total?: number;
}

export interface OrderStatusBreakdown {
    pending?: number;
    confirmed?: number;
    processing?: number;
    shipped?: number;
    delivered?: number;
    cancelled?: number;
    refunded?: number;
}

export interface CustomerSegmentation {
    vip?: number;
    active?: number;
    occasional?: number;
    atRisk?: number;
    lost?: number;
    newCustomers?: number;
}

export interface ProductPerformance {
    productId?: string;
    name?: string;
    quantitySold?: number;
    revenue?: number;
    views?: number;
    conversionRate?: number;
    imageUrl?: string;
}

export interface CategoryPerformance {
    categoryId?: string;
    name?: string;
    revenue?: number;
    quantitySold?: number;
    marketShare?: number;
    trend?: string;
}

export interface DailyDataPoint {
    date?: string;
    value?: number;
    label?: string;
}

export interface Resource extends InputStreamSource {
    open?: boolean;
    file?: any;
    readable?: boolean;
    url?: URL;
    description?: string;
    uri?: URI;
    filename?: string;
    contentAsByteArray?: any;
}

export interface InputStreamSource {
    inputStream?: any;
}

export interface CartEntity extends AbstractEntity, Serializable {
    order?: OrderEntity;
    product?: ProductEntity;
    quantity?: number;
    commented?: boolean;
}

export interface CustomerEntity extends UserEntity {
    addresses?: AddressEntity[];
    orders?: OrderEntity[];
}

export interface CouponEntity extends AbstractEntity, Serializable {
    code?: string;
    type?: CouponType;
    discount?: number;
    fixedAmount?: number;
    minimumOrderAmount?: number;
    maxUsages?: number;
    currentUsages?: number;
    startDate?: Date;
    expiryDate?: Date;
    used?: boolean;
    user?: UserEntity;
    useDate?: Date;
}

export interface DeliveryEntity extends AbstractEntity, Serializable {
    deliveryCode?: string;
    courrierName?: string;
    courrierPhone?: string;
    customerName?: string;
    customerEmail?: string;
    customerPhone?: string;
    customerAdditionalPhone?: string;
    apartmentInfo?: string;
    streetAddress?: string;
    neighborhood?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    status?: DeliveryStatus;
    deliveryDate?: Date;
    shippingDate?: Date;
    order?: OrderEntity;
}

export interface PaymentEntity extends AbstractEntity {
    customerName?: string;
    customerEmail?: string;
    amount?: number;
    paymentMethod?: PaymentMethod;
    status?: PaymentStatus;
    paymentDate?: Date;
    transactionId?: string;
    provider?: string;
    phoneNumber?: string;
    redirectUrl?: string;
    paymentToken?: string;
    reference?: string;
    description?: string;
    errorMessage?: string;
    cancellationReason?: string;
    refundedAmount?: number;
    refundDate?: Date;
    order?: OrderEntity;
}

export interface OrderTrackingStepEntity extends AbstractEntity, Serializable {
    order?: OrderEntity;
    status?: OrderStatus;
    trackingDate?: Date;
    actor?: string;
    comment?: string;
}

export interface OrderAddressEntity extends AbstractEntity, Serializable {
    label?: string;
    fullName?: string;
    phone?: string;
    additionalPhone?: string;
    streetAddress?: string;
    apartmentInfo?: string;
    neighborhood?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    latitude?: number;
    longitude?: number;
    deliveryInstructions?: string;
    order?: OrderEntity;
    fullAddressText?: string;
}

export interface AbstractEntity extends Serializable {
    id?: string;
    createdAt?: Date;
    updatedAt?: Date;
    deleted?: boolean;
    deletedAt?: Date;
}

export interface URL extends Serializable {
}

export interface URI extends Comparable<URI>, Serializable {
}

export interface ProductEntity extends AbstractEntity, Serializable {
    name?: string;
    slug?: string;
    sku?: string;
    description?: string;
    price?: number;
    status?: ProductStatusEnum;
    type?: ProductType;
    published?: boolean;
    pageSections?: PageSectionEntity[];
    imagePath?: string;
    stock?: number;
    sold?: number;
    views?: number;
    rated?: number;
    discountedPercent?: number;
    metaTitle?: string;
    metaKeywords?: string;
    metaDescription?: string;
    category?: CategoryEntity;
    stockEntities?: StockEntity[];
    carts?: CartEntity[];
    ratings?: RatingEntity[];
    wishes?: WishEntity[];
    productAttributes?: ProductAttributeEntity[];
    medias?: MediaEntity[];
    variants?: ProductVariantEntity[];
    hasVariants?: boolean;
    new?: boolean;
    featured?: boolean;
    bestSeller?: boolean;
    topRated?: boolean;
    defaultVariant?: ProductVariantEntity;
    availableVariants?: ProductVariantEntity[];
}

export interface TokenEntity extends AbstractEntity, Serializable {
    token?: string;
    loggedOut?: boolean;
    user?: UserEntity;
}

export interface AddressEntity extends AbstractEntity, Serializable {
    label?: string;
    addressType?: AddressType;
    phone?: string;
    additionalPhone?: string;
    streetAddress?: string;
    apartmentInfo?: string;
    neighborhood?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    latitude?: number;
    longitude?: number;
    usageCount?: number;
    customer?: CustomerEntity;
    default?: boolean;
    fullAddressText?: string;
    verified?: boolean;
}

export interface SimpleGrantedAuthority extends GrantedAuthority {
}

export interface UserEntity extends AbstractEntity, UserDetails, Serializable {
    fullName?: string;
    email?: string;
    phone?: string;
    reasonDisabled?: string;
    lastConnection?: Date;
    tokens?: TokenEntity[];
    role?: RoleEnum;
    authorities?: SimpleGrantedAuthority[];
    mainIdentifier?: string;
}

export interface PageSectionEntity extends AbstractEntity, Serializable {
    title?: string;
    type?: SectionType;
    slug?: string;
    description?: string;
    position?: number;
    published?: boolean;
    banners?: BannerEntity[];
    products?: ProductEntity[];
    bannerCount?: number;
}

export interface CategoryEntity extends AbstractEntity, Serializable {
    name?: string;
    description?: string;
    categoryType?: CategoryType;
    position?: number;
    parentCategory?: CategoryEntity;
    categoryCard?: boolean;
    cardPosition?: number;
    slug?: string;
    mainMenu?: boolean;
}

export interface StockEntity extends AbstractEntity, Serializable {
    product?: ProductEntity;
    quantity?: number;
    action?: StockEnum;
}

export interface RatingEntity extends AbstractEntity, Serializable {
    customer?: CustomerEntity;
    rating?: number;
    review?: string;
    visible?: boolean;
}

export interface WishEntity extends AbstractEntity, Serializable {
    customer?: CustomerEntity;
    product?: ProductEntity;
}

export interface ProductAttributeEntity extends AbstractEntity, Serializable {
    product?: ProductEntity;
    attribute?: AttributeEntity;
    value?: string;
}

export interface MediaEntity extends AbstractEntity, Serializable {
    product?: ProductEntity;
    title?: string;
    mimeType?: string;
    content?: any;
    fileName?: string;
    originalFileName?: string;
    defaulted?: boolean;
    displayOrder?: number;
    width?: number;
    height?: number;
    fileSize?: number;
}

export interface ProductVariantEntity extends AbstractEntity, Serializable {
    product?: ProductEntity;
    sku?: string;
    name?: string;
    price?: number;
    compareAtPrice?: number;
    stock?: number;
    sold?: number;
    weight?: number;
    barcode?: string;
    available?: boolean;
    displayOrder?: number;
    imageUrl?: string;
    attributes?: ProductVariantAttributeEntity[];
    default?: boolean;
    effectivePrice?: number;
    inStock?: boolean;
}

export interface GrantedAuthority extends Serializable {
    authority?: string;
}

export interface UserDetails extends Serializable {
    enabled?: boolean;
    username?: string;
    password?: string;
    authorities?: GrantedAuthority[];
    accountNonExpired?: boolean;
    accountNonLocked?: boolean;
    credentialsNonExpired?: boolean;
}

export interface Comparable<T> {
}

export interface BannerEntity extends Serializable {
    url?: string;
    type?: string;
    empty?: boolean;
}

export interface AttributeEntity extends AbstractEntity, Serializable {
    attributeName?: string;
    attributeCategory?: string;
    description?: string;
    productAttributes?: ProductAttributeEntity[];
}

export interface ProductVariantAttributeEntity extends AbstractEntity, Serializable {
    productVariant?: ProductVariantEntity;
    attributeValue?: AttributeValueEntity;
}

export interface AttributeValueEntity extends AbstractEntity, Serializable {
    attributeType?: AttributeTypeEntity;
    code?: string;
    value?: string;
    colorHex?: string;
    imageUrl?: string;
    displayOrder?: number;
    active?: boolean;
    productVariantAttributes?: ProductVariantAttributeEntity[];
}

export interface AttributeTypeEntity extends AbstractEntity, Serializable {
    code?: string;
    name?: string;
    description?: string;
    displayOrder?: number;
    usedForVariants?: boolean;
    visibleOnFront?: boolean;
    values?: AttributeValueEntity[];
}

export enum AuthEventType {
    REGISTRATION = "REGISTRATION",
    LOGIN = "LOGIN",
    LOGOUT = "LOGOUT",
    PROFILE_UPDATE = "PROFILE_UPDATE",
    PASSWORD_RESET = "PASSWORD_RESET",
    FAILED_LOGIN = "FAILED_LOGIN",
}

export enum IdentifierType {
    EMAIL = "EMAIL",
    PHONE = "PHONE",
    EMAIL_AND_PHONE = "EMAIL_AND_PHONE",
}

export enum RoleEnum {
    ADMIN = "ADMIN",
    CUSTOMER = "CUSTOMER",
    DELIVERER = "DELIVERER",
    SUPPORT = "SUPPORT",
}

export enum ActionEnum {
    ADD = "ADD",
    REMOVE = "REMOVE",
}

export enum CategoryLevel {
    TOP_LEVEL = "TOP_LEVEL",
    SECOND_LEVEL = "SECOND_LEVEL",
    THIRD_LEVEL = "THIRD_LEVEL",
}

export enum CategoryType {
    PRODUCT = "PRODUCT",
    CONTENT = "CONTENT",
    COLLECTION = "COLLECTION",
}

export enum ProductDiscountType {
    NONE = "NONE",
    PERCENTAGE = "PERCENTAGE",
    AMOUNT = "AMOUNT",
}

export enum ProductPositionEnum {
    MAIN_MOST = "MAIN_MOST",
    MAIN_CATEGORY = "MAIN_CATEGORY",
    PAGE_PRODUCT = "PAGE_PRODUCT",
}

export enum ProductStatusEnum {
    DRAFT = "DRAFT",
    PUBLISHED = "PUBLISHED",
    SCHEDULED = "SCHEDULED",
}

export enum ProductType {
    PHYSICAL = "PHYSICAL",
    DIGITAL = "DIGITAL",
}

export enum SpecificityTypeEnum {
    NUMERIC = "NUMERIC",
    STRING = "STRING",
    SUGGESTION = "SUGGESTION",
}

export enum StockEnum {
    SOLD = "SOLD",
    ORDER = "ORDER",
    ADDED = "ADDED",
    SPOIL = "SPOIL",
    INITIAL_STOCK = "INITIAL_STOCK",
}

export enum CouponType {
    PERCENTAGE = "PERCENTAGE",
    FIXED_AMOUNT = "FIXED_AMOUNT",
    FREE_SHIPPING = "FREE_SHIPPING",
    BUY_X_GET_Y = "BUY_X_GET_Y",
}

export enum DeliveryMode {
    HOME_DELIVERY = "HOME_DELIVERY",
    RELAY_POINT = "RELAY_POINT",
    STORE_PICKUP = "STORE_PICKUP",
}

export enum DeliveryStatus {
    CREATED = "CREATED",
    IN_TRANSIT = "IN_TRANSIT",
    DELIVERED = "DELIVERED",
    FAILED = "FAILED",
}

export enum OrderStatus {
    PENDING = "PENDING",
    CONFIRMED = "CONFIRMED",
    PREPARING = "PREPARING",
    SHIPPED = "SHIPPED",
    DELIVERED = "DELIVERED",
    COMPLETED = "COMPLETED",
    RETURNED = "RETURNED",
    CANCELLED = "CANCELLED",
    FAILED = "FAILED",
}

export enum SubscriberType {
    GUEST = "GUEST",
    CUSTOMER = "CUSTOMER",
    ADMIN = "ADMIN",
}

export enum AddressType {
    SHIPPING = "SHIPPING",
    BILLING = "BILLING",
    BOTH = "BOTH",
}

export enum BannerType {
    IMAGE = "IMAGE",
    YOUTUBE = "YOUTUBE",
}

export enum SectionType {
    HERO = "HERO",
    FEATURED_PRODUCTS = "FEATURED_PRODUCTS",
    CATEGORIES = "CATEGORIES",
    BANNER = "BANNER",
    TESTIMONIALS = "TESTIMONIALS",
    OTHER = "OTHER",
}

export enum PaymentMethod {
    CREDIT_CARD = "CREDIT_CARD",
    MOBILE_MONEY = "MOBILE_MONEY",
    CASH_ON_DELIVERY = "CASH_ON_DELIVERY",
    BANK_TRANSFER = "BANK_TRANSFER",
    CASH = "CASH",
    ONLINE = "ONLINE",
}

export enum PaymentStatus {
    PENDING = "PENDING",
    PROCESSING = "PROCESSING",
    PAID = "PAID",
    COMPLETED = "COMPLETED",
    FAILED = "FAILED",
    CANCELLED = "CANCELLED",
    REFUNDED = "REFUNDED",
    PARTIALLY_REFUNDED = "PARTIALLY_REFUNDED",
}

export enum ComplaintCategoryEnum {
    PRODUCT_DEFECTIVE = "PRODUCT_DEFECTIVE",
    PRODUCT_NOT_AS_DESCRIBED = "PRODUCT_NOT_AS_DESCRIBED",
    DELIVERY_DELAY = "DELIVERY_DELAY",
    DELIVERY_LOST = "DELIVERY_LOST",
    DELIVERY_DAMAGED = "DELIVERY_DAMAGED",
    WRONG_ITEM = "WRONG_ITEM",
    MISSING_ITEM = "MISSING_ITEM",
    CUSTOMER_SERVICE = "CUSTOMER_SERVICE",
    BILLING = "BILLING",
    REFUND = "REFUND",
    QUALITY = "QUALITY",
    OTHER = "OTHER",
}

export enum ComplaintPriorityEnum {
    LOW = "LOW",
    MEDIUM = "MEDIUM",
    HIGH = "HIGH",
    URGENT = "URGENT",
}

export enum ComplaintStatusEnum {
    NEW = "NEW",
    ASSIGNED = "ASSIGNED",
    IN_PROGRESS = "IN_PROGRESS",
    AWAITING_CUSTOMER = "AWAITING_CUSTOMER",
    RESOLVED = "RESOLVED",
    CLOSED = "CLOSED",
    CANCELLED = "CANCELLED",
}

export enum AdvertisementPositionEnum {
    ANNOUNCEMENT = "ANNOUNCEMENT",
    CAROUSEL = "CAROUSEL",
    BANNER_LARGE = "BANNER_LARGE",
    BANNER_SHORT_LEFT = "BANNER_SHORT_LEFT",
    BANNER_SHORT_RIGHT = "BANNER_SHORT_RIGHT",
}
